import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MdAdd,
  MdArrowDownward,
  MdArrowUpward,
  MdAttachFile,
  MdChevronLeft,
  MdChevronRight,
  MdClearAll,
  MdDelete,
  MdDownload,
  MdEdit,
  MdFileUpload,
  MdFilterAlt,
  MdFirstPage,
  MdLastPage,
  MdOpenInNew,
  MdRefresh,
  MdSearch,
  MdStorage,
  MdUnfoldMore,
  MdInsights,
} from 'react-icons/md';
import { API_URL, authenticatedFetch } from '../../lib/api';
import {
  ensureFilenameExtension,
  downloadFromResponse,
  downloadRemoteFile,
  triggerBlobDownload,
} from '../../utils/download';
import { useToast } from '../../components/shared/Toast';
import { Modal } from '../../components/shared/Modal';

export interface PartFinderFile {
  _id: string;
  fileName: string;
  fileUrl: string;
  fileType?: string;
  docType?: string;
  uploadedAt?: string;
}

export interface PartFinderRow {
  _id: string;
  name: string;
  partNumber: string;
  impaNumber?: string;
  position?: string;
  drawingNumber?: string;
  files?: PartFinderFile[];
  categoryName?: string;
  subCategoryName?: string;
  brandName?: string;
  modelName?: string;
  modelId?: string;
  categoryId?: string;
  subCategoryId?: string;
}

type SortKey = keyof PartFinderRow;
type SortDir = 'asc' | 'desc';

type ColumnDef = {
  key: SortKey;
  label: string;
  width: string;
  mono?: boolean;
  numeric?: boolean;
};

const COLUMNS: ColumnDef[] = [
  { key: 'categoryName', label: 'Category', width: '160px' },
  { key: 'subCategoryName', label: 'Sub-Category', width: '160px' },
  { key: 'brandName', label: 'Brand', width: '160px' },
  { key: 'modelName', label: 'Model', width: '160px' },
  { key: 'name', label: 'Description', width: '180px' },
  { key: 'partNumber', label: 'Part Number', width: '160px', mono: true },
  { key: 'position', label: 'Position Number', width: '140px', mono: true },
  { key: 'drawingNumber', label: 'Drawing Number', width: '140px', mono: true },
  { key: 'impaNumber', label: 'IMPA Number', width: '140px', mono: true },
];

const PAGE_SIZE = 10;

const EMPTY_FILTERS = (): Record<string, string> =>
  Object.fromEntries(COLUMNS.map((c) => [c.key, '']));

function buildPartFinderQuery(
  extra: Record<string, string | number | undefined>,
  paging?: { page: number; limit: number }
) {
  const params = new URLSearchParams();
  if (paging) {
    params.set('page', String(paging.page));
    params.set('limit', String(paging.limit));
  }
  for (const [key, value] of Object.entries(extra)) {
    if (value === undefined || value === null || String(value).trim() === '') continue;
    params.set(key, String(value));
  }
  return params.toString();
}

function cellValue(row: PartFinderRow, key: SortKey): string {
  const v = row[key];
  if (v === undefined || v === null) return '';
  return String(v);
}

function exportCsv(rows: PartFinderRow[]) {
  const headers = COLUMNS.map((c) => c.label);
  const lines = rows.map((row) =>
    COLUMNS.map((c) => {
      const val = cellValue(row, c.key);
      return `"${val.replace(/"/g, '""')}"`;
    }).join(','),
  );
  const blob = new Blob([[headers.join(','), ...lines].join('\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  triggerBlobDownload(
    blob,
    `partfinder-export-${new Date().toISOString().slice(0, 10)}.csv`,
    '.csv',
  );
}

function resolveMediaUrl(url?: string) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const base = (API_URL || '').replace(/\/$/, '');
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${base}${path}`;
}

function mapPartDocumentsToFiles(part: any): PartFinderFile[] {
  return (part?.documents || []).map((doc: any) => ({
    _id: doc._id?.toString?.() || String(doc._id || ''),
    fileName: doc.fileName,
    fileUrl: doc.fileUrl,
    fileType: doc.fileType || '',
    docType: doc.docType || 'General',
    uploadedAt: doc.uploadedAt,
  }));
}

interface PartFinderRegistryProps {
  onRefreshHierarchy?: () => void;
}

export function PartFinderRegistry({ onRefreshHierarchy }: PartFinderRegistryProps) {
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [rows, setRows] = useState<PartFinderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(EMPTY_FILTERS);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('partNumber');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [debouncedGlobal, setDebouncedGlobal] = useState('');
  const [debouncedCols, setDebouncedCols] = useState<Record<string, string>>(EMPTY_FILTERS);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState<PartFinderRow | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    partNumber: '',
    impaNumber: '',
    position: '',
    drawingNumber: '',
  });
  const [saving, setSaving] = useState(false);
  const [pendingVariations, setPendingVariations] = useState(0);

  const [filesOpen, setFilesOpen] = useState(false);
  const [filesRow, setFilesRow] = useState<PartFinderRow | null>(null);
  const [filesList, setFilesList] = useState<PartFinderFile[]>([]);
  const [filesUploading, setFilesUploading] = useState(false);
  const [renamingFileId, setRenamingFileId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const filesInputRef = useRef<HTMLInputElement>(null);

  const fetchRegistry = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const qs = buildPartFinderQuery(
        {
          search: debouncedGlobal.trim() || undefined,
          categoryName: debouncedCols.categoryName,
          subCategoryName: debouncedCols.subCategoryName,
          brandName: debouncedCols.brandName,
          modelName: debouncedCols.modelName,
          name: debouncedCols.name,
          partNumber: debouncedCols.partNumber,
          position: debouncedCols.position,
          drawingNumber: debouncedCols.drawingNumber,
          impaNumber: debouncedCols.impaNumber,
          sortKey,
          sortDir,
        },
        { page, limit: PAGE_SIZE }
      );
      const res = await authenticatedFetch(`/api/v1/admin/product-hierarchy/partfinder?${qs}`, { signal });
      if (signal?.aborted) return;
      if (!res.ok) throw new Error('Failed to load PartFinder registry');
      const json = await res.json();
      if (signal?.aborted) return;
      setRows(Array.isArray(json.data) ? json.data : []);
      const total = Number(json.pagination?.total ?? json.total ?? 0) || 0;
      const pages = Number(json.pagination?.totalPages) || Math.max(1, Math.ceil(total / PAGE_SIZE) || 1);
      setTotalItems(total);
      setTotalPages(pages);
    } catch (err: any) {
      if (err?.name === 'AbortError' || signal?.aborted) return;
      showToast(err.message || 'Could not load PartFinder databank', 'error');
      setRows([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [showToast, page, debouncedGlobal, debouncedCols, sortKey, sortDir]);

  useEffect(() => {
    const filtersEqual =
      globalFilter === debouncedGlobal &&
      COLUMNS.every((col) => (columnFilters[col.key] || '') === (debouncedCols[col.key] || ''));
    if (filtersEqual) return;
    const timer = setTimeout(() => {
      setDebouncedGlobal(globalFilter);
      setDebouncedCols(columnFilters);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [globalFilter, columnFilters, debouncedGlobal, debouncedCols]);

  useEffect(() => {
    const ac = new AbortController();
    void fetchRegistry(ac.signal);
    return () => ac.abort();
  }, [fetchRegistry]);

  useEffect(() => {
    const ac = new AbortController();
    authenticatedFetch('/api/v1/admin/product-hierarchy/partfinder/analytics', { signal: ac.signal })
      .then((r) => r.json())
      .then((j) => {
        if (ac.signal.aborted) return;
        if (j.success) setPendingVariations(j.data?.pending || 0);
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
      });
    return () => ac.abort();
  }, []);

  const pageRows = rows;
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handleExportCsv = async () => {
    try {
      const qs = buildPartFinderQuery({
        search: globalFilter.trim() || undefined,
        categoryName: columnFilters.categoryName,
        subCategoryName: columnFilters.subCategoryName,
        brandName: columnFilters.brandName,
        modelName: columnFilters.modelName,
        name: columnFilters.name,
        partNumber: columnFilters.partNumber,
        position: columnFilters.position,
        drawingNumber: columnFilters.drawingNumber,
        impaNumber: columnFilters.impaNumber,
        sortKey,
        sortDir,
      });
      const res = await authenticatedFetch(`/api/v1/admin/product-hierarchy/partfinder?${qs}`);
      if (!res.ok) throw new Error('Failed to export PartFinder registry');
      const json = await res.json();
      exportCsv(Array.isArray(json.data) ? json.data : []);
    } catch (err: any) {
      showToast(err.message || 'Could not export PartFinder databank', 'error');
    }
  };

  const activeFilterCount =
    Object.values(columnFilters).filter((v) => v.trim()).length + (globalFilter.trim() ? 1 : 0);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const clearFilters = () => {
    setColumnFilters(EMPTY_FILTERS());
    setGlobalFilter('');
    setPage(1);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === pageRows.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pageRows.map((r) => r._id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openEdit = (row: PartFinderRow) => {
    setEditRow(row);
    setEditForm({
      name: row.name || '',
      partNumber: row.partNumber,
      impaNumber: row.impaNumber || '',
      position: row.position || '',
      drawingNumber: row.drawingNumber || '',
    });
    setEditOpen(true);
  };

  const openNew = () => {
    setEditRow(null);
    setEditForm({
      name: '',
      partNumber: '',
      impaNumber: '',
      position: '',
      drawingNumber: '',
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!editForm.partNumber.trim()) {
      showToast('Part number is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const partName = editForm.name.trim() || editForm.partNumber.trim();
      const payload = {
        name: partName,
        partNumber: editForm.partNumber.trim(),
        msfNumber: editForm.impaNumber.trim() || undefined,
        position: editForm.position.trim() || undefined,
        drawing: editForm.drawingNumber.trim() || undefined,
      };

      const url = editRow
        ? `/api/v1/admin/product-hierarchy/parts/${editRow._id}`
        : '/api/v1/admin/product-hierarchy/parts';
      const res = await authenticatedFetch(url, {
        method: editRow ? 'PUT' : 'POST',
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Save failed');

      showToast(editRow ? 'Part updated in PartFinder' : 'Part added to PartFinder', 'success');
      setEditOpen(false);
      await fetchRegistry();
      onRefreshHierarchy?.();
    } catch (err: any) {
      showToast(err.message || 'Failed to save part', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: PartFinderRow) => {
    if (!window.confirm(`Delete part ${row.partNumber} from PartFinder?`)) return;
    try {
      const res = await authenticatedFetch(`/api/v1/admin/product-hierarchy/parts/${row._id}`, {
        method: 'DELETE',
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Delete failed');
      showToast('Part removed from registry', 'success');
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(row._id);
        return next;
      });
      await fetchRegistry();
      onRefreshHierarchy?.();
    } catch (err: any) {
      showToast(err.message || 'Failed to delete part', 'error');
    }
  };

  const openFiles = (row: PartFinderRow) => {
    setFilesRow(row);
    setFilesList(row.files || []);
    setRenamingFileId(null);
    setRenameValue('');
    setFilesOpen(true);
  };

  const syncFilesIntoRows = (partId: string, files: PartFinderFile[]) => {
    setRows((prev) =>
      prev.map((r) => (r._id === partId ? { ...r, files } : r)),
    );
    setFilesList(files);
    setFilesRow((prev) => (prev && prev._id === partId ? { ...prev, files } : prev));
  };

  const handleUploadFiles = async (fileList: FileList | null) => {
    if (!filesRow || !fileList?.length) return;
    setFilesUploading(true);
    try {
      const fd = new FormData();
      Array.from(fileList).forEach((f) => fd.append('documents', f));
      const res = await authenticatedFetch(
        `/api/v1/admin/product-hierarchy/parts/${filesRow._id}/documents`,
        { method: 'POST', body: fd },
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Upload failed');
      const files = mapPartDocumentsToFiles(json.data);
      syncFilesIntoRows(filesRow._id, files);
      showToast(`${fileList.length} file(s) uploaded`, 'success');
      onRefreshHierarchy?.();
    } catch (err: any) {
      showToast(err.message || 'Failed to upload files', 'error');
    } finally {
      setFilesUploading(false);
      if (filesInputRef.current) filesInputRef.current.value = '';
    }
  };

  const handleDeleteFile = async (file: PartFinderFile) => {
    if (!filesRow) return;
    if (!window.confirm(`Delete file "${file.fileName}"?`)) return;
    try {
      const res = await authenticatedFetch(
        `/api/v1/admin/product-hierarchy/parts/${filesRow._id}/documents/${file._id}`,
        { method: 'DELETE' },
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Delete failed');
      const files = mapPartDocumentsToFiles(json.data);
      syncFilesIntoRows(filesRow._id, files);
      showToast('File deleted', 'success');
      onRefreshHierarchy?.();
    } catch (err: any) {
      showToast(err.message || 'Failed to delete file', 'error');
    }
  };

  const handleRenameFile = async (file: PartFinderFile) => {
    if (!filesRow) return;
    const nextName = renameValue.trim();
    if (!nextName) {
      showToast('File name is required', 'error');
      return;
    }
    try {
      const res = await authenticatedFetch(
        `/api/v1/admin/product-hierarchy/parts/${filesRow._id}/documents/${file._id}`,
        {
          method: 'PUT',
          body: JSON.stringify({ fileName: nextName }),
        },
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Rename failed');
      const files = mapPartDocumentsToFiles(json.data);
      syncFilesIntoRows(filesRow._id, files);
      setRenamingFileId(null);
      setRenameValue('');
      showToast('File renamed', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to rename file', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected part(s)?`)) return;
    let ok = 0;
    for (const id of selectedIds) {
      try {
        const res = await authenticatedFetch(`/api/v1/admin/product-hierarchy/parts/${id}`, {
          method: 'DELETE',
        });
        if (res.ok) ok++;
      } catch {
        /* continue */
      }
    }
    showToast(`Deleted ${ok} of ${selectedIds.size} part(s)`, ok ? 'success' : 'error');
    setSelectedIds(new Set());
    await fetchRegistry();
    onRefreshHierarchy?.();
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const base =
        import.meta.env.DEV && !import.meta.env.VITE_API_URL
          ? ''
          : API_URL || 'http://localhost:3000';
      const res = await fetch(`${base}/api/v1/admin/product-hierarchy/parts/bulk-template`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Download failed');
      await downloadFromResponse(res, 'partfinder-bulk-template.xlsx', '.xlsx');
    } catch (err: any) {
      showToast(err.message || 'Could not download template', 'error');
    }
  };

  const handleBulkUpload = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('accessToken');
      const base =
        import.meta.env.DEV && !import.meta.env.VITE_API_URL
          ? ''
          : API_URL || 'http://localhost:3000';
      const res = await fetch(`${base}/api/v1/admin/product-hierarchy/parts/bulk-upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Import failed');
      const created = json.data?.created ?? 0;
      const failed = json.data?.failed ?? 0;
      showToast(`Import complete: ${created} created, ${failed} failed`, 'success');
      await fetchRegistry();
      onRefreshHierarchy?.();
    } catch (err: any) {
      showToast(err.message || 'Bulk import failed', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* PartFinder hero strip */}
      <div className="rounded-2xl border border-[#0088cc]/25 bg-gradient-to-r from-[#0088cc]/10 via-[hsl(var(--card))] to-teal-500/10 p-5 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#0088cc] text-white flex items-center justify-center shadow-lg shrink-0">
              <MdStorage className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-lg font-black text-[hsl(var(--foreground))] flex items-center gap-2">
                PartFinder
                <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#0088cc]/15 text-[#0088cc] border border-[#0088cc]/30">
                  Global Registry
                </span>
              </h2>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl leading-relaxed">
                Global parts registry — category, optional sub-category, brand, model, part number,
                position, drawing, IMPA, and attached files (any format). Pricing and stock stay with each company.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-center items-center">
            <Link
              to="/dashboard/admin/partfinder-intelligence"
              className="px-4 py-2 rounded-xl bg-purple-600/10 border border-purple-500/30 text-purple-700 dark:text-purple-300 text-xs font-bold flex items-center gap-2 hover:bg-purple-600/20 transition"
            >
              <MdInsights className="w-4 h-4" />
              Data Comparisons
              {pendingVariations > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px]">
                  {pendingVariations}
                </span>
              )}
            </Link>
            <div className="px-4 py-2 rounded-xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] min-w-[90px]">
              <p className="text-lg font-black text-[#0088cc]">{rows.length.toLocaleString()}</p>
              <p className="text-[10px] font-bold uppercase text-[hsl(var(--muted-foreground))]">Total Parts</p>
            </div>
            <div className="px-4 py-2 rounded-xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] min-w-[90px]">
              <p className="text-lg font-black text-teal-600">{totalItems.toLocaleString()}</p>
              <p className="text-[10px] font-bold uppercase text-[hsl(var(--muted-foreground))]">Filtered</p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3 p-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
        <div className="flex-1 relative max-w-md">
          <MdSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <input
            type="text"
            value={globalFilter}
            onChange={(e) => {
              setGlobalFilter(e.target.value);
              setPage(1);
            }}
            placeholder="Search entire registry…"
            className="w-full pl-9 pr-4 py-2 bg-[hsl(var(--background))] border border-[hsl(var(--border))] rounded-xl text-xs outline-none focus:border-[#0088cc]"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={openNew}
            className="px-3 py-2 rounded-xl bg-[#0088cc] text-white text-xs font-bold flex items-center gap-1 hover:bg-[#0077b3]"
          >
            <MdAdd className="w-4 h-4" /> Add Part
          </button>
          <button
            type="button"
            onClick={() => {
              const first = pageRows.find((r) => selectedIds.has(r._id));
              if (first) openEdit(first);
              else showToast('Select a row to edit', 'error');
            }}
            disabled={selectedIds.size !== 1}
            className="px-3 py-2 rounded-xl border border-[hsl(var(--border))] text-xs font-bold flex items-center gap-1 disabled:opacity-40"
          >
            <MdEdit className="w-4 h-4" /> Edit
          </button>
          <button
            type="button"
            onClick={handleBulkDelete}
            disabled={selectedIds.size === 0}
            className="px-3 py-2 rounded-xl border border-rose-200 text-rose-600 text-xs font-bold flex items-center gap-1 disabled:opacity-40"
          >
            <MdDelete className="w-4 h-4" /> Delete ({selectedIds.size})
          </button>
          <span className="w-px h-6 bg-[hsl(var(--border))] hidden sm:block" />
          <button
            type="button"
            onClick={downloadTemplate}
            className="px-3 py-2 rounded-xl border border-[hsl(var(--border))] text-xs font-bold flex items-center gap-1"
          >
            <MdDownload className="w-4 h-4" /> Template
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="px-3 py-2 rounded-xl border border-[hsl(var(--border))] text-xs font-bold flex items-center gap-1"
          >
            <MdFileUpload className="w-4 h-4" /> {uploading ? 'Importing…' : 'Import Excel'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleBulkUpload(f);
            }}
          />
          <button
            type="button"
            onClick={() => void handleExportCsv()}
            className="px-3 py-2 rounded-xl border border-[hsl(var(--border))] text-xs font-bold flex items-center gap-1"
          >
            <MdDownload className="w-4 h-4" /> Export CSV
          </button>
          <button
            type="button"
            onClick={clearFilters}
            className="px-3 py-2 rounded-xl border border-[hsl(var(--border))] text-xs font-bold flex items-center gap-1"
          >
            <MdClearAll className="w-4 h-4" /> Clear Filters
            {activeFilterCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#0088cc] text-white text-[10px]">
                {activeFilterCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => void fetchRegistry()}
            className="p-2 rounded-xl border border-[hsl(var(--border))]"
            title="Refresh registry"
          >
            <MdRefresh className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Spreadsheet grid */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm overflow-hidden">
        <div className="overflow-hidden min-h-[480px] max-h-[520px]">
          <table className="w-full border-collapse text-xs min-w-[1750px]">
            <thead className="sticky top-0 z-20">
              <tr className="bg-[hsl(var(--muted))] border-b border-[hsl(var(--border))]">
                <th className="sticky left-0 z-30 bg-[hsl(var(--muted))] w-10 px-2 py-2 border-r border-[hsl(var(--border))]">
                  <input
                    type="checkbox"
                    checked={pageRows.length > 0 && selectedIds.size === pageRows.length}
                    onChange={toggleSelectAll}
                    className="rounded"
                  />
                </th>
                <th className="sticky left-10 z-30 bg-[hsl(var(--muted))] w-10 px-2 py-2 text-[10px] font-bold text-[hsl(var(--muted-foreground))] border-r border-[hsl(var(--border))]">
                  #
                </th>
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    style={{ minWidth: col.width }}
                    className="px-2 py-2 text-left border-r border-[hsl(var(--border))]"
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort(col.key)}
                      className="flex items-center gap-1 font-bold uppercase text-[10px] tracking-wide text-[hsl(var(--muted-foreground))] hover:text-[#0088cc] w-full"
                    >
                      {col.label}
                      {sortKey === col.key ? (
                        sortDir === 'asc' ? (
                          <MdArrowUpward className="w-3.5 h-3.5 text-[#0088cc]" />
                        ) : (
                          <MdArrowDownward className="w-3.5 h-3.5 text-[#0088cc]" />
                        )
                      ) : (
                        <MdUnfoldMore className="w-3.5 h-3.5 opacity-40" />
                      )}
                    </button>
                  </th>
                ))}
                <th className="px-2 py-2 text-left border-r border-[hsl(var(--border))] min-w-[120px] text-[10px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                  Files
                </th>
                <th className="sticky right-0 z-40 bg-[hsl(var(--muted))] px-2 py-2 text-[10px] font-bold uppercase min-w-[96px] w-24 border-l border-[hsl(var(--border))] shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.15)]">
                  Actions
                </th>
              </tr>
              <tr className="bg-[hsl(var(--background))] border-b-2 border-[#0088cc]/30">
                <th className="sticky left-0 z-30 bg-[hsl(var(--background))] px-2 py-1.5 border-r border-[hsl(var(--border))]">
                  <MdFilterAlt className="w-3.5 h-3.5 text-[#0088cc] mx-auto" />
                </th>
                <th className="sticky left-10 z-30 bg-[hsl(var(--background))] border-r border-[hsl(var(--border))]" />
                {COLUMNS.map((col) => (
                  <th
                    key={`filter-${col.key}`}
                    className="px-1 py-1 border-r border-[hsl(var(--border))]"
                  >
                    <input
                      type="text"
                      value={columnFilters[col.key] || ''}
                      onChange={(e) => {
                        setColumnFilters((prev) => ({ ...prev, [col.key]: e.target.value }));
                        setPage(1);
                      }}
                      placeholder="Filter…"
                      className="w-full px-2 py-1 rounded-md bg-[hsl(var(--card))] border border-[hsl(var(--border))] text-[10px] outline-none focus:border-[#0088cc]"
                    />
                  </th>
                ))}
                <th className="border-r border-[hsl(var(--border))]" />
                <th className="sticky right-0 z-40 bg-[hsl(var(--background))] border-l border-[hsl(var(--border))] shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.15)]" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={COLUMNS.length + 4} className="py-16 text-center text-[hsl(var(--muted-foreground))]">
                    Loading PartFinder registry…
                  </td>
                </tr>
              ) : pageRows.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length + 4} className="py-16 text-center text-[hsl(var(--muted-foreground))]">
                    No parts match your filters. Clear filters or import data to grow the databank.
                  </td>
                </tr>
              ) : (
                pageRows.map((row, idx) => {
                  const selected = selectedIds.has(row._id);
                  const rowBg = selected
                    ? 'bg-[#e6f4fb] dark:bg-[#0b2a3a]'
                    : idx % 2 === 0
                      ? 'bg-[hsl(var(--card))]'
                      : 'bg-[hsl(var(--muted))]/40';
                  const fileCount = row.files?.length || 0;
                  return (
                    <tr
                      key={row._id}
                      className={`border-b border-[hsl(var(--border))] transition hover:bg-[#0088cc]/5 ${rowBg}`}
                    >
                      <td className={`sticky left-0 z-10 px-2 py-2 border-r border-[hsl(var(--border))] ${rowBg}`}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleSelect(row._id)}
                          className="rounded"
                        />
                      </td>
                      <td className={`sticky left-10 z-10 px-2 py-2 text-[10px] text-[hsl(var(--muted-foreground))] font-mono border-r border-[hsl(var(--border))] ${rowBg}`}>
                        {(safePage - 1) * PAGE_SIZE + idx + 1}
                      </td>
                      {COLUMNS.map((col) => {
                        const raw = cellValue(row, col.key);
                        const display = raw || '—';
                        return (
                          <td
                            key={col.key}
                            className={`px-2 py-2 border-r border-[hsl(var(--border))] truncate max-w-[200px] ${
                              col.mono ? 'font-mono' : ''
                            } ${col.key === 'partNumber' ? 'font-bold text-[#0088cc]' : ''}`}
                            title={raw}
                          >
                            {display}
                          </td>
                        );
                      })}
                      <td className="px-2 py-1.5 border-r border-[hsl(var(--border))]">
                        <button
                          type="button"
                          onClick={() => openFiles(row)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide text-[#0088cc] hover:bg-[#0088cc]/10"
                          title="Manage files"
                        >
                          <MdAttachFile className="w-3.5 h-3.5" />
                          {fileCount > 0 ? `${fileCount} file${fileCount === 1 ? '' : 's'}` : 'Upload'}
                        </button>
                      </td>
                      <td
                        className={`sticky right-0 z-20 px-1.5 py-1 border-l border-[hsl(var(--border))] shadow-[-6px_0_8px_-6px_rgba(0,0,0,0.15)] ${rowBg}`}
                      >
                        <div className="flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => openEdit(row)}
                            title="Edit part"
                            aria-label="Edit part"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[#0088cc] hover:bg-[#0088cc]/10"
                          >
                            <MdEdit className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(row)}
                            title="Delete part"
                            aria-label="Delete part"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-rose-200 bg-[hsl(var(--card))] text-rose-600 hover:bg-rose-50 dark:border-rose-800 dark:hover:bg-rose-950/40"
                          >
                            <MdDelete className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination — left-aligned with bottom padding so floating chat does not cover Next */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start gap-3 px-4 py-3 pb-16 border-t border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30">
          <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
            <span>
              Showing {totalItems === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, totalItems)} of{' '}
              {totalItems.toLocaleString()}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={safePage <= 1}
              className="p-1.5 rounded-lg border border-[hsl(var(--border))] disabled:opacity-40"
            >
              <MdFirstPage className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="p-1.5 rounded-lg border border-[hsl(var(--border))] disabled:opacity-40"
            >
              <MdChevronLeft className="w-5 h-5" />
            </button>
            <span className="px-3 text-xs font-bold">
              Page {safePage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="p-1.5 rounded-lg border border-[hsl(var(--border))] disabled:opacity-40"
            >
              <MdChevronRight className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={safePage >= totalPages}
              className="p-1.5 rounded-lg border border-[hsl(var(--border))] disabled:opacity-40"
            >
              <MdLastPage className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        title={editRow ? 'Edit Part — PartFinder' : 'Add Part — PartFinder'}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block text-xs font-bold text-[hsl(var(--muted-foreground))] sm:col-span-2">
            Description
            <input
              type="text"
              value={editForm.name}
              onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Description (admin can set freely)"
              className="mt-1 w-full px-3.5 py-2.5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-sm font-medium shadow-sm focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20 outline-none transition-all"
            />
          </label>
          {(
            [
              ['partNumber', 'Part Number', 'text'],
              ['position', 'Position Number', 'text'],
              ['drawingNumber', 'Drawing Number', 'text'],
              ['impaNumber', 'IMPA Number', 'text'],
            ] as const
          ).map(([key, label, type]) => (
            <label key={key} className="block text-xs font-bold text-[hsl(var(--muted-foreground))]">
              {label}
              {key === 'partNumber' ? ' *' : ''}
              <input
                type={type}
                value={String(editForm[key as keyof typeof editForm] ?? '')}
                onChange={(e) =>
                  setEditForm((prev) => ({
                    ...prev,
                    [key]: e.target.value,
                  }))
                }
                className="mt-1 w-full px-3.5 py-2.5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-sm font-medium shadow-sm focus:border-[#0088cc] focus:ring-2 focus:ring-[#0088cc]/20 outline-none transition-all"
              />
            </label>
          ))}
        </div>
        {editRow && (
          <div className="mt-3 p-3 rounded-xl bg-[hsl(var(--muted))]/40 text-xs grid grid-cols-2 gap-2">
            <span>
              <strong>Category:</strong> {editRow.categoryName || '—'}
            </span>
            <span>
              <strong>Sub-Category:</strong> {editRow.subCategoryName || '—'}
            </span>
            <span>
              <strong>Brand:</strong> {editRow.brandName || '—'}
            </span>
            <span>
              <strong>Model:</strong> {editRow.modelName || '—'}
            </span>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={() => setEditOpen(false)}
            className="px-4 py-2 rounded-xl border border-[hsl(var(--border))] text-xs font-bold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-xl bg-[#0088cc] text-white text-xs font-bold disabled:opacity-60"
          >
            {saving ? 'Saving…' : editRow ? 'Update Part' : 'Add to Registry'}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={filesOpen}
        onClose={() => {
          setFilesOpen(false);
          setRenamingFileId(null);
          setRenameValue('');
        }}
        title={
          filesRow
            ? `Files — ${filesRow.partNumber || filesRow.name || 'Part'}`
            : 'Part Files'
        }
      >
        <input
          ref={filesInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleUploadFiles(e.target.files)}
        />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            Upload any format (images, PDF, Excel, CAD, etc.). Up to 20 files, 50MB each.
          </p>
          <button
            type="button"
            disabled={filesUploading || !filesRow}
            onClick={() => filesInputRef.current?.click()}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-[#0088cc] text-white text-xs font-bold hover:bg-[#0077b3] disabled:opacity-60 shrink-0"
          >
            <MdFileUpload className="w-4 h-4" />
            {filesUploading ? 'Uploading…' : 'Upload Files'}
          </button>
        </div>

        {filesList.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[hsl(var(--border))] py-10 text-center text-xs text-[hsl(var(--muted-foreground))]">
            No files yet. Click Upload Files to attach documents to this part.
          </div>
        ) : (
          <ul className="space-y-2 max-h-[360px] overflow-auto pr-1">
            {filesList.map((file) => {
              const href = resolveMediaUrl(file.fileUrl);
              const isRenaming = renamingFileId === file._id;
              return (
                <li
                  key={file._id}
                  className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 px-3 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    {isRenaming ? (
                      <input
                        type="text"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRenameFile(file);
                          if (e.key === 'Escape') {
                            setRenamingFileId(null);
                            setRenameValue('');
                          }
                        }}
                        className="w-full px-2 py-1.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] text-sm font-medium outline-none focus:border-[#0088cc]"
                        autoFocus
                      />
                    ) : (
                      <>
                        <div className="text-sm font-semibold truncate" title={file.fileName}>
                          {file.fileName}
                        </div>
                        <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">
                          {[file.fileType, file.docType].filter(Boolean).join(' · ') || 'Attachment'}
                          {file.uploadedAt
                            ? ` · ${new Date(file.uploadedAt).toLocaleDateString()}`
                            : ''}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {isRenaming ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handleRenameFile(file)}
                          className="px-2.5 py-1.5 rounded-lg bg-[#0088cc] text-white text-[10px] font-bold"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setRenamingFileId(null);
                            setRenameValue('');
                          }}
                          className="px-2.5 py-1.5 rounded-lg border border-[hsl(var(--border))] text-[10px] font-bold"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        {href ? (
                          <>
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Open"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[#0088cc] hover:bg-[#0088cc]/10"
                            >
                              <MdOpenInNew className="w-4 h-4" />
                            </a>
                            <button
                              type="button"
                              title="Download"
                              onClick={() => {
                                void downloadRemoteFile(
                                  href,
                                  ensureFilenameExtension(
                                    file.fileName || 'document',
                                    file.fileType,
                                    '.pdf',
                                  ),
                                  {
                                    contentType: file.fileType,
                                    fallbackExt: '.pdf',
                                  },
                                ).catch((err: any) =>
                                  showToast(err?.message || 'Download failed', 'error'),
                                );
                              }}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[#0088cc] hover:bg-[#0088cc]/10"
                            >
                              <MdDownload className="w-4 h-4" />
                            </button>
                          </>
                        ) : null}
                        <button
                          type="button"
                          title="Rename"
                          onClick={() => {
                            setRenamingFileId(file._id);
                            setRenameValue(file.fileName);
                          }}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[#0088cc] hover:bg-[#0088cc]/10"
                        >
                          <MdEdit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          title="Delete"
                          onClick={() => handleDeleteFile(file)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-rose-200 bg-[hsl(var(--card))] text-rose-600 hover:bg-rose-50 dark:border-rose-800 dark:hover:bg-rose-950/40"
                        >
                          <MdDelete className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex justify-end mt-5">
          <button
            type="button"
            onClick={() => {
              setFilesOpen(false);
              setRenamingFileId(null);
              setRenameValue('');
            }}
            className="px-4 py-2 rounded-xl border border-[hsl(var(--border))] text-xs font-bold"
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
}
